import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import './LicenseExpirationNotifications.css';

const LicenseExpirationNotifications = ({ user }) => {
  const { t } = useTranslation();
  const [licenseStatus, setLicenseStatus] = useState(null);
  const [showNotification, setShowNotification] = useState(false);
  const [notificationType, setNotificationType] = useState('');
  const [loading, setLoading] = useState(true);

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

  useEffect(() => {
    if (user) {
      checkLicenseStatus();
      // Vérifier le statut toutes les 5 minutes
      const interval = setInterval(checkLicenseStatus, 5 * 60 * 1000);
      return () => clearInterval(interval);
    }
  }, [user]);

  const checkLicenseStatus = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/license/my-license`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setLicenseStatus(data);
        determineNotificationType(data);
      }
    } catch (error) {
      console.error(t('licenseExpirationNotifications.errors.checkLicense'), error);
    } finally {
      setLoading(false);
    }
  };

  const determineNotificationType = (status) => {
    if (!status.hasLicense) {
      setNotificationType('no-license');
      setShowNotification(true);
      return;
    }

    const license = status.license;
    const daysRemaining = license.daysRemaining;

    if (license.isExpired) {
      setNotificationType('expired');
      setShowNotification(true);
    } else if (daysRemaining <= 3) {
      setNotificationType('critical');
      setShowNotification(true);
    } else if (daysRemaining <= 7) {
      setNotificationType('warning');
      setShowNotification(true);
    } else if (daysRemaining <= 15 && license.type === 'free_trial') {
      setNotificationType('info');
      setShowNotification(true);
    } else {
      setShowNotification(false);
    }
  };

  const getNotificationConfig = () => {
    const days = licenseStatus?.license?.daysRemaining;
    const expiredDate = licenseStatus?.license?.endDate;
    
    switch (notificationType) {
      case 'no-license':
        return {
          icon: t('licenseExpirationNotifications.notifications.noLicense.icon'),
          title: t('licenseExpirationNotifications.notifications.noLicense.title'),
          message: t('licenseExpirationNotifications.notifications.noLicense.message'),
          actionText: t('licenseExpirationNotifications.notifications.noLicense.actionText'),
          actionUrl: t('licenseExpirationNotifications.notifications.noLicense.actionUrl'),
          className: 'notification-info',
          priority: 'high'
        };

      case 'expired':
        return {
          icon: t('licenseExpirationNotifications.notifications.expired.icon'),
          title: t('licenseExpirationNotifications.notifications.expired.title'),
          message: t('licenseExpirationNotifications.notifications.expired.message', { date: expiredDate }),
          actionText: t('licenseExpirationNotifications.notifications.expired.actionText'),
          actionUrl: t('licenseExpirationNotifications.notifications.expired.actionUrl'),
          className: 'notification-critical',
          priority: 'critical'
        };

      case 'critical':
        return {
          icon: t('licenseExpirationNotifications.notifications.critical.icon'),
          title: t('licenseExpirationNotifications.notifications.critical.title', { days }),
          message: t('licenseExpirationNotifications.notifications.critical.message'),
          actionText: t('licenseExpirationNotifications.notifications.critical.actionText'),
          actionUrl: t('licenseExpirationNotifications.notifications.critical.actionUrl'),
          className: 'notification-critical',
          priority: 'critical'
        };

      case 'warning':
        return {
          icon: t('licenseExpirationNotifications.notifications.warning.icon'),
          title: t('licenseExpirationNotifications.notifications.warning.title', { days }),
          message: t('licenseExpirationNotifications.notifications.warning.message'),
          actionText: t('licenseExpirationNotifications.notifications.warning.actionText'),
          actionUrl: t('licenseExpirationNotifications.notifications.warning.actionUrl'),
          className: 'notification-warning',
          priority: 'medium'
        };

      case 'info':
        return {
          icon: t('licenseExpirationNotifications.notifications.info.icon'),
          title: t('licenseExpirationNotifications.notifications.info.title', { days }),
          message: t('licenseExpirationNotifications.notifications.info.message'),
          actionText: t('licenseExpirationNotifications.notifications.info.actionText'),
          actionUrl: t('licenseExpirationNotifications.notifications.info.actionUrl'),
          className: 'notification-info',
          priority: 'low'
        };

      default:
        return null;
    }
  };

  const handleActionClick = (url) => {
    if (url === '/trial-activation') {
      startFreeTrial();
    } else {
      window.location.href = url;
    }
  };

  const startFreeTrial = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/license/start-trial`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        toast.success(t('licenseExpirationNotifications.trialActivated'));
        checkLicenseStatus(); // Recharger le statut
      } else {
        const error = await response.json();
        toast.error(`❌ Erreur: ${error.error || 'Impossible d\'activer le trial'}`);
      }
    } catch (error) {
      console.error('Erreur activation trial:', error);
      toast.error('❌ Erreur technique lors de l\'activation du trial');
    }
  };

  const dismissNotification = () => {
    setShowNotification(false);
    // Stocker le dismiss pour ne pas réafficher immédiatement
    const dismissTime = Date.now();
    localStorage.setItem(`license-notification-dismiss-${notificationType}`, dismissTime.toString());
    
    // Réafficher après un délai selon la priorité
    const delays = {
      'critical': 1 * 60 * 1000, // 1 minute pour critique
      'warning': 30 * 60 * 1000, // 30 minutes pour warning
      'info': 2 * 60 * 60 * 1000 // 2 heures pour info
    };
    
    const config = getNotificationConfig();
    if (config && delays[config.priority]) {
      setTimeout(() => {
        setShowNotification(true);
      }, delays[config.priority]);
    }
  };

  const wasRecentlyDismissed = () => {
    const dismissTime = localStorage.getItem(`license-notification-dismiss-${notificationType}`);
    if (!dismissTime) return false;
    
    const timeSinceDismiss = Date.now() - parseInt(dismissTime);
    const minDismissTime = 5 * 60 * 1000; // 5 minutes minimum
    
    return timeSinceDismiss < minDismissTime;
  };

  if (loading || !showNotification || wasRecentlyDismissed()) {
    return null;
  }

  const config = getNotificationConfig();
  if (!config) return null;

  return (
    <div className={`license-expiration-notification ${config.className}`}>
      <div className="notification-content">
        <div className="notification-icon">{config.icon}</div>
        <div className="notification-text">
          <h4>{config.title}</h4>
          <p>{config.message}</p>
        </div>
        <div className="notification-actions">
          <button 
            className="btn-action primary"
            onClick={() => handleActionClick(config.actionUrl)}
          >
            {config.actionText}
          </button>
          <button 
            className="btn-dismiss"
            onClick={dismissNotification}
            title={t('licenseExpirationNotifications.tooltips.dismiss')}
          >
            ✕
          </button>
        </div>
      </div>
      
      {/* Barre de progression pour l'expiration */}
      {licenseStatus?.license && !licenseStatus.license.isExpired && (
        <div className="expiration-progress">
          <div className="progress-bar">
            <div 
              className="progress-fill"
              style={{ 
                width: `${Math.max(0, (licenseStatus.license.daysRemaining / 30) * 100)}%` 
              }}
            ></div>
          </div>
          <span className="progress-text">
            {t('licenseExpirationNotifications.progress.daysRemaining', { days: licenseStatus.license.daysRemaining })}
          </span>
        </div>
      )}
    </div>
  );
};

export default LicenseExpirationNotifications;
